'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const COOLDOWN_MS = 30 * 1000;
const STORAGE_KEY = 'lastBlogFetchTime';

export function BlogFetchButton() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    const checkCooldown = () => {
      const storedTime = localStorage.getItem(STORAGE_KEY);
      if (storedTime) {
        const lastFetchTime = parseInt(storedTime, 10);
        const elapsed = Date.now() - lastFetchTime;

        if (elapsed < COOLDOWN_MS) {
          setIsOnCooldown(true);
          setRemainingSeconds(Math.ceil((COOLDOWN_MS - elapsed) / 1000));
        } else {
          setIsOnCooldown(false);
          setRemainingSeconds(0);
        }
      }
    };

    checkCooldown();

    const interval = setInterval(() => {
      if (isOnCooldown || localStorage.getItem(STORAGE_KEY)) {
        checkCooldown();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isOnCooldown]);

  const handleFetchArticle = useCallback(async () => {
    const storedTime = localStorage.getItem(STORAGE_KEY);
    if (storedTime) {
      const lastFetchTime = parseInt(storedTime, 10);
      const elapsed = Date.now() - lastFetchTime;

      if (elapsed < COOLDOWN_MS) {
        const remaining = Math.ceil((COOLDOWN_MS - elapsed) / 1000);
        toast.warning(`لطفاً ${remaining} ثانیه صبر کنید`);
        return;
      }
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/arxiv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ category: 'cs.AI' }),
      });

      const data = await response.json();

      if (data.success && data.isNew) {
        toast.success(data.message || 'مقاله جدید با موفقیت اضافه شد!');
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
        setIsOnCooldown(true);
        setRemainingSeconds(30);
        router.refresh();
      } else if (data.reason === 'all_exist') {
        toast.info(data.message || 'همه مقالات اخیر قبلاً در سایت وجود دارند');
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
        setIsOnCooldown(true);
        setRemainingSeconds(30);
      } else if (data.reason === 'race_condition') {
        toast.info(data.message || 'این مقاله قبلاً اضافه شده است');
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
        setIsOnCooldown(true);
        setRemainingSeconds(30);
      } else {
        toast.error(data.message || 'خطا در دریافت مقاله');
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      toast.error('خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.');
    } finally {
      setIsLoading(false);
    }
  }, [router]);

  const isDisabled = isLoading || isOnCooldown;

  return (
    <button
      onClick={handleFetchArticle}
      disabled={isDisabled}
      className={`
        inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
        transition-all duration-200
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#01ADEF]
        ${isDisabled
          ? 'bg-white/10 text-white/50 cursor-not-allowed border border-white/10'
          : 'bg-white text-[#08075C] hover:bg-[#01ADEF] hover:text-white border border-white/20 shadow-sm'
        }
      `}
      title={isOnCooldown ? `لطفاً ${remainingSeconds} ثانیه صبر کنید` : 'دریافت مقاله جدید از arXiv'}
    >
      {isLoading ? (
        <>
          <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" />
          <span>در حال دریافت…</span>
        </>
      ) : isOnCooldown ? (
        <>
          <Clock className="w-4 h-4" aria-hidden="true" />
          <span>صبر کنید ({remainingSeconds}s)</span>
        </>
      ) : (
        <>
          <RefreshCw className="w-4 h-4" aria-hidden="true" />
          <span>دریافت مقاله جدید</span>
        </>
      )}
    </button>
  );
}
